import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'

import {
  useClassifierServiceGetCommonClassifierElementEndpointKey,
  useClassifierServiceGetSystemKeyListEndpoint
} from 'src/api/queries'
import { ClassifierService, SystemKeyItem } from 'src/api/requests'
import { useLocales } from 'src/locales'

/**
 * System keys (Form 058-1/e) an admin can map to the elements of a classifier,
 * with the names of the elements currently holding the unique ones.
 */
export const useSystemKeys = (classifierId?: string) => {
  const { currentLang } = useLocales()

  const { data, isLoading } = useClassifierServiceGetSystemKeyListEndpoint(
    { id: String(classifierId) },
    undefined,
    { enabled: !!classifierId }
  )

  const keys = useMemo(() => data?.data ?? [], [data])

  const holderIds = useMemo(
    () =>
      Array.from(
        new Set(
          keys
            .map((item) => item.holderElementId)
            .filter((holderId): holderId is string => !!holderId)
        )
      ),
    [keys]
  )

  // Holders can sit on another page of the element list, so their names are fetched by id.
  const holders = useQueries({
    queries: holderIds.map((elementId) => ({
      queryKey: [
        useClassifierServiceGetCommonClassifierElementEndpointKey,
        { id: classifierId, elementId }
      ],
      queryFn: () =>
        ClassifierService.getCommonClassifierElementEndpoint(
          String(classifierId),
          elementId
        )
    }))
  })

  const holderNames = useMemo(() => {
    const names: Record<string, string> = {}

    holders.forEach(({ data: holder }) => {
      if (holder?.id)
        names[holder.id] =
          holder.translation?.[currentLang.value] ??
          holder.translation?.ro ??
          ''
    })

    return names
  }, [holders, currentLang.value])

  const getLabel = (item?: SystemKeyItem) =>
    item?.label?.[currentLang.value] ?? item?.label?.ro ?? item?.key

  const findKey = (value?: string | null) =>
    value ? keys.find((item) => item.key === value) : undefined

  return {
    keys,
    hasKeys: keys.length > 0,
    isLoading,
    holderNames,
    getLabel,
    findKey
  }
}
